import React, { useState } from 'react';
import styled from 'styled-components';

import CardActionModal from './CardActionModal';

import Headline3 from '@joinforma/ui/components/Headline3';
import Body2 from '@joinforma/ui/components/Body2';
import Button from '@joinforma/ui/components/Button';
import { BUTTON_SIZES } from '@joinforma/ui/constants/properties';
import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

import DEBIT_CARD from 'assets/images/enabled-debitcard.svg';

import { FormaCardMember } from './types';
import IssueFormaCardForm from './IssueFormaCardForm';

import useMutationCreateFormaCard from 'hooks/useMutationCreateFormaCard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 72px;
`;

const BenefitsInformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 64px;
`;

const Title = styled(Headline3)``;

const Description = styled(Body2)`
  margin: 12px 0 24px;
`;

const IssueCardButton = styled(Button).attrs(() => ({
  size: BUTTON_SIZES.LARGE,
}))`
  width: 277px;
  height: 48px;
  margin: 12px 0;
  font-size: 15px;
`;

const DebitCardContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const DebitCard = styled.img`
  width: 175px;
  height: 108px;
  transform: rotate(-21.46deg);
`;

const IssueCard = ({
  forma_card_member,
  mailing_address,
  employee_id,
  refetchCards,
}: {
  forma_card_member: FormaCardMember;
  employee_id: string;
  mailing_address: FormaCardMember['billing_address'];
  refetchCards: () => void;
}) => {
  const [is_issue_card_modal_open, setIsIssueCardModalOpen] = useState(false);

  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

  const { mutate: issuePhysicalCard } = useMutationCreateFormaCard({
    onSuccess: () => {
      refetchCards();
      showSuccessSnackbar({
        text: 'A new card has been mailed to the address you provided.',
      });
    },
    onError: () => {
      showErrorSnackbar({ text: 'An error occurred. Please try again.' });
    },
  });
  const handleCancel = () => setIsIssueCardModalOpen(false);

  const is_dependent = employee_id !== forma_card_member.id;
  const subtitle = is_dependent
    ? "Dependent's Benefits Debit Card Information"
    : '';

  const { first_name } = forma_card_member;
  return (
    <Container>
      <DebitCardContainer>
        <DebitCard src={DEBIT_CARD} alt="Forma Card" />
      </DebitCardContainer>
      <BenefitsInformationContainer>
        {is_dependent ? (
          <Title>{`Issue a dependent benefits card for ${first_name}`}</Title>
        ) : (
          <Title>Issue my benefits card</Title>
        )}
        {is_dependent && (
          <Description>
            {`Allow ${first_name} to use a benefits card to spend funds from your benefits accounts.`}
          </Description>
        )}
        <IssueCardButton onClick={() => setIsIssueCardModalOpen(true)}>
          Issue card
        </IssueCardButton>
        <CardActionModal
          is_open={is_issue_card_modal_open}
          title={`Add ${first_name}'s address info to issue the card`}
          subtitle={subtitle}
          onCancel={handleCancel}
        >
          <IssueFormaCardForm
            forma_card_member={forma_card_member}
            mailing_address={mailing_address}
            handleCreateFormaCard={issuePhysicalCard}
            handleCancel={handleCancel}
            employee_id={employee_id}
          />
        </CardActionModal>
      </BenefitsInformationContainer>
    </Container>
  );
};

export default IssueCard;
